import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { selectUser } from '../store/slices/authSlice.js';
import { toast } from 'react-hot-toast';
import { Plus, Save, Check, Package, Search, ChevronDown, Eye, Users } from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout.jsx';
import { CREATE_SHIPMENT_EVENT_MUTATION, COMPLETE_SHIPMENT_MUTATION } from '../services/graphql/mutations.js';
import { ACTIVE_SHIPMENTS_FOR_UPDATES_QUERY, GET_RECIPIENTS_PREVIEW_QUERY, CHECK_PENDING_EVENTS_QUERY } from '../services/graphql/queries.js';
import EventUpdatesRecipientsModal from '../components/dashboard/EventUpdatesRecipientsModal.jsx';
import CompleteShipmentRecipientsModal from '../components/dashboard/CompleteShipmentRecipientsModal.jsx';
import PendingEventsWarningModal from '../components/dashboard/PendingEventsWarningModal.jsx';

const EventUpdates = () => {
  const user = useSelector(selectUser);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [statusDetail, setStatusDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showShipmentSelector, setShowShipmentSelector] = useState(true);
  const [showRecipientsPreview, setShowRecipientsPreview] = useState(false);
  const [showCompleteShipmentModal, setShowCompleteShipmentModal] = useState(false);
  const [showPendingEventsModal, setShowPendingEventsModal] = useState(false);
  const [pendingEventsData, setPendingEventsData] = useState(null);

  // GraphQL Queries
  const { data: shipmentsData, loading: shipmentsLoading, refetch } = useQuery(ACTIVE_SHIPMENTS_FOR_UPDATES_QUERY, {
    fetchPolicy: 'cache-and-network'
  });

  // Query for recipients preview (when shipment is selected)
  const { data: recipientsData } = useQuery(GET_RECIPIENTS_PREVIEW_QUERY, {
    variables: { manifest: selectedShipment?.manifest },
    skip: !selectedShipment?.manifest,
    fetchPolicy: 'cache-and-network'
  });

  // Query for checking pending events (when shipment is selected)
  const { data: pendingEventsCheckData, loading: pendingEventsLoading } = useQuery(CHECK_PENDING_EVENTS_QUERY, {
    variables: { manifest: selectedShipment?.manifest },
    skip: !selectedShipment?.manifest,
    fetchPolicy: 'cache-and-network'
  });

  // GraphQL Mutations
  // El toast de éxito lo muestran handleSubmit/handleCompleteShipment con el
  // conteo de destinatarios (L-7: antes se mostraban dos toasts y el conteo
  // sumaba emails duplicados entre BLs)
  const [createShipmentEvent] = useMutation(CREATE_SHIPMENT_EVENT_MUTATION, {
    onCompleted: (data) => {
      setStatusDetail('');
      setSelectedShipment(null);
      setShowShipmentSelector(true);
      setIsSubmitting(false); // ✅ Resetear estado isSubmitting en éxito
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al agregar el evento');
      setIsSubmitting(false);
    }
  });

  const [completeShipment] = useMutation(COMPLETE_SHIPMENT_MUTATION, {
    onCompleted: (data) => {
      setStatusDetail('');
      setSelectedShipment(null);
      setShowShipmentSelector(true);
      setIsSubmitting(false);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Error al finalizar el manifiesto');
      setIsSubmitting(false);
    }
  });

  // Conteo de destinatarios únicos (un mismo email seleccionado en varios BLs
  // recibe un correo por BL, pero como "destinatario" se cuenta una sola vez)
  const countUniqueEmails = (groups) => {
    const unique = new Set();
    (groups || []).forEach(group =>
      (group.selectedEmails || []).forEach(email => unique.add(email.toLowerCase()))
    );
    return unique.size;
  };

  // Filter shipments based on user role and search term
  const filteredShipments = React.useMemo(() => {
    if (!shipmentsData?.activeShipmentsForUpdates) return [];
    
    let shipments = shipmentsData.activeShipmentsForUpdates;
    
    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      shipments = shipments.filter(shipment =>
        shipment.manifest?.toLowerCase().includes(searchLower) ||
        shipment.reference?.toLowerCase().includes(searchLower) ||
        shipment.origin?.origin_name?.toLowerCase().includes(searchLower) ||
        shipment.destination?.destination_name?.toLowerCase().includes(searchLower) ||
        shipment.details?.some(detail => 
          detail.consignee?.full_name?.toLowerCase().includes(searchLower)
        )
      );
    }
    
    return shipments;
  }, [shipmentsData?.activeShipmentsForUpdates, searchTerm]);

  // Function to check for pending events
  const checkForPendingEvents = () => {
    if (!selectedShipment?.manifest) {
      toast.error('Por favor selecciona un envío');
      return false;
    }

    if (pendingEventsLoading) {
      toast.error('Verificando eventos pendientes...');
      return false;
    }

    const pendingCheck = pendingEventsCheckData?.checkPendingEvents;
    
    if (pendingCheck?.hasPendingEvents) {
      setPendingEventsData({
        manifest: selectedShipment.manifest,
        pendingEvents: pendingCheck.pendingEvents || [],
        pendingEventsCount: pendingCheck.pendingEventsCount || 0
      });
      setShowPendingEventsModal(true);
      return false;
    }

    return true;
  };

  // Handlers
  const handleShipmentSelect = (shipment) => {
    setSelectedShipment(shipment);
    setShowShipmentSelector(false);
  };

  const handlePreviewRecipients = () => {
    if (!statusDetail.trim()) {
      toast.error('Por favor ingresa los detalles del estado');
      return;
    }
    if (!selectedShipment?.manifest) {
      toast.error('Por favor selecciona un envío');
      return;
    }

    // Check for pending events before continuing
    if (!checkForPendingEvents()) {
      return;
    }

    setShowRecipientsPreview(true);
  };

  const handleSubmit = async (approved = false, sendEmails = false, selectedRecipients = null) => {
    if (!statusDetail.trim()) {
      toast.error('Por favor ingresa los detalles del estado');
      return;
    }

    if (!selectedShipment?.manifest) {
      toast.error('Por favor selecciona un envío');
      return;
    }

    // Only check for pending events if this is not an approved event with emails
    // (because approved events with emails are handled through the preview modal)
    if (!approved || !sendEmails) {
      if (!checkForPendingEvents()) {
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const inputData = {
        manifest: selectedShipment.manifest,
        status_detail: statusDetail.trim(),
        status_date: new Date().toISOString(),
        approved: approved
      };

      // Add selected emails if this is an approved event with selected recipients
      if (approved && sendEmails && selectedRecipients && selectedRecipients.length > 0) {
        
        // selectedRecipients now comes as granular structure from modal
        const selectedRecipientsByGroup = Array.isArray(selectedRecipients) ? selectedRecipients : [];
        
        // Map granular structure directly to detailsWithEmails
        // Matching por bill_of_lading (no por groupIndex posicional) porque selectedShipment.details
        // y getRecipientsPreview son dos consultas independientes a BD con orden no garantizado.
        const detailsWithEmails = selectedRecipientsByGroup.map(group => {
          const shipmentDetail = selectedShipment.details.find(
            d => d.bill_of_lading === group.bill_of_lading
          );

          if (!shipmentDetail) {
            console.warn(`⚠️ No shipment detail found for bill_of_lading ${group.bill_of_lading}`);
            return null;
          }

          return {
            bill_of_lading: group.bill_of_lading,
            commercial_invoice: shipmentDetail.commercial_invoice,
            consigneeId: shipmentDetail.consigneeId,
            supplierIds: shipmentDetail.suppliers ? shipmentDetail.suppliers.map(s => s.id) : [],
            ff: shipmentDetail.ff,
            cif: shipmentDetail.cif,
            additional_consignees: shipmentDetail.additional_consignees || null,
            seller: shipmentDetail.seller || null,
            selectedEmails: group.selectedEmails
          };
        }).filter(detail => detail !== null); // Remove any null entries
        
        
        // Use granular structure if we have email selections
        if (detailsWithEmails.length > 0) {
          inputData.detailsWithEmails = detailsWithEmails;
          
        } else {
          // Fallback to simple email list if granular processing fails
          const allEmails = selectedRecipientsByGroup.flatMap(group => group.selectedEmails);
          inputData.selectedEmails = allEmails;
        }
      }


      await createShipmentEvent({
        variables: {
          input: inputData
        }
      });

      // Un solo toast de éxito por camino (L-7)
      if (sendEmails && approved) {
        toast.success(`Evento aprobado. Se enviarán correos automáticamente a ${countUniqueEmails(selectedRecipients)} destinatario(s).`, {
          duration: 5000
        });
      } else {
        toast.success('Evento agregado exitosamente');
      }
    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);
      // Error handled by mutation onError
    }
  };

  const handleCompleteShipment = async (selectedRecipients = null) => {
    if (!statusDetail.trim()) {
      toast.error('Por favor ingresa los detalles del estado final');
      return;
    }

    if (!selectedShipment?.manifest) {
      toast.error('Por favor selecciona un envío');
      return;
    }

    // Check for pending events before completing shipment
    if (!checkForPendingEvents()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const inputData = {
        manifest: selectedShipment.manifest,
        status_detail: statusDetail.trim(),
        status_date: new Date().toISOString()
      };

      // Add selected emails if provided
      if (selectedRecipients && selectedRecipients.length > 0) {
        // Map granular structure directly to detailsWithEmails
        // Matching por bill_of_lading (no por groupIndex posicional) — misma razón que handleSubmit:
        // selectedShipment y getRecipientsPreview son consultas independientes a BD con orden no garantizado.
        const detailsWithEmails = selectedRecipients.map(group => {
          const shipmentDetail = selectedShipment.details.find(
            d => d.bill_of_lading === group.bill_of_lading
          );

          if (!shipmentDetail) {
            console.warn(`⚠️ No shipment detail found for bill_of_lading ${group.bill_of_lading}`);
            return null;
          }

          return {
            bill_of_lading: group.bill_of_lading,
            commercial_invoice: shipmentDetail.commercial_invoice,
            consigneeId: shipmentDetail.consigneeId,
            supplierIds: shipmentDetail.suppliers ? shipmentDetail.suppliers.map(s => s.id) : [],
            ff: shipmentDetail.ff,
            cif: shipmentDetail.cif,
            additional_consignees: shipmentDetail.additional_consignees || null,
            seller: shipmentDetail.seller || null,
            selectedEmails: group.selectedEmails
          };
        }).filter(detail => detail !== null); // Remove any null entries
        
        if (detailsWithEmails.length > 0) {
          inputData.detailsWithEmails = detailsWithEmails;
        }
      }

      await completeShipment({
        variables: {
          input: inputData
        }
      });

      // Un solo toast de éxito con el conteo de destinatarios únicos (L-7)
      if (selectedRecipients && selectedRecipients.length > 0) {
        toast.success(`Manifiesto finalizado. Se enviaron correos a ${countUniqueEmails(selectedRecipients)} destinatario(s).`, {
          duration: 5000
        });
      } else {
        toast.success('Manifiesto finalizado exitosamente. El envío se ha marcado como completado.');
      }

    } catch (error) {
      console.error('❌ Error in handleCompleteShipment:', error);
      // Error handled by mutation onError
    }
  };

  const handleCancel = () => {
    setStatusDetail('');
    setSelectedShipment(null);
    setShowShipmentSelector(true);
  };

  const isAdmin = user?.role?.role_name === 'Administrador';
  const isTransportista = user?.role?.role_name === 'Transportista';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4">
            <Check className="h-6 w-6 mr-3 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Actualizar Estados</h1>
              <p className="text-gray-600">Gestiona y actualiza los estados de los envíos</p>
            </div>
          </div>

          {/* Información del rol */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Información:</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Selecciona un envío activo para agregar un nuevo evento de estado</li>
              <li>• El evento se registrará con la fecha y hora actual</li>
              {isTransportista && (
                <li>• Los eventos que crees requerirán aprobación del administrador para notificar a los clientes</li>
              )}
              {isTransportista && (
                <li>• Como Transportista solo puedes actualizar envíos en donde NO seas un FF</li>
              )}              
              {isAdmin && (
                <li>• Puedes guardar sin aprobar o aprobar inmediatamente para enviar notificaciones automáticas por email</li>
              )}
            </ul>
          </div>
        </div>

        {/* Shipment Selector */}
        {showShipmentSelector && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Package className="h-5 w-5 mr-2 text-blue-600" />
                Seleccionar Envío
              </h3>
            </div>

            <div className="p-6">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por Manifiesto, Referencia, Origen, Destino o Consignatario..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Shipments List */}
              {shipmentsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Cargando envíos...</p>
                </div>
              ) : filteredShipments.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No se encontraron envíos activos</p>
                  {searchTerm && <p className="text-gray-400 text-sm mt-2">Intenta ajustar tu búsqueda</p>}
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredShipments.map((shipment) => (
                    <div
                      key={shipment.manifest}
                      onClick={() => handleShipmentSelect(shipment)}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <span className="font-medium text-gray-900">
                              Manifest: {shipment.manifest}
                            </span>
                            <span className="text-sm text-gray-600">
                              Ref: {shipment.reference}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <span>{shipment.origin?.origin_name || 'N/A'}</span>
                            <span className="mx-2">→</span>
                            <span>{shipment.destination?.destination_name || 'N/A'}</span>
                          </div>
                        </div>
                        <ChevronDown className="h-5 w-5 text-gray-400 transform rotate-[-90deg]" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Event Form */}
        {selectedShipment && !showShipmentSelector && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Plus className="h-5 w-5 mr-2 text-green-600" />
                  Agregar Nuevo Evento
                </h3>
                <button
                  onClick={handleCancel}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Cambiar envío
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Shipment Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Información del Envío Seleccionado</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Manifiesto:</span>
                    <span className="ml-2 font-medium">{selectedShipment.manifest}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Referencia:</span>
                    <span className="ml-2 font-medium">{selectedShipment.reference}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Origen:</span>
                    <span className="ml-2 font-medium">{selectedShipment.origin?.origin_name || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Destino:</span>
                    <span className="ml-2 font-medium">{selectedShipment.destination?.destination_name || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Status Detail Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Detalles del Estado *
                </label>
                <textarea
                  value={statusDetail}
                  onChange={(e) => setStatusDetail(e.target.value)}
                  placeholder="Ingresa los detalles del nuevo estado del envío..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  disabled={isSubmitting}
                  maxLength={500}
                />
                <div className="text-right text-xs text-gray-500 mt-1">
                  {statusDetail.length}/500 caracteres
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-gray-200">
                {/* Botón Cancelar */}
                <button
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Cancelar
                </button>

                {/* Botones para Transportista */}
                {isTransportista && (
                  <button
                    onClick={() => handleSubmit(false, false)}
                    disabled={isSubmitting || !statusDetail.trim()}
                    className="flex items-center px-6 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                  </button>
                )}

                {/* Botones para Administrador */}
                {isAdmin && (
                  <>
                    <button
                      onClick={() => handleSubmit(false, false)}
                      disabled={isSubmitting || !statusDetail.trim()}
                      className="flex items-center px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isSubmitting ? 'Guardando...' : 'Guardar y aprobar luego'}
                    </button>
                    <button
                      onClick={handlePreviewRecipients}
                      disabled={isSubmitting || !statusDetail.trim()}
                      className="flex items-center px-6 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      <Users className="h-4 w-4 mr-1" />
                      Visualiza destinatarios y envía correos
                    </button>
                  </>
                )}

                {/* Botón Marcar como completado - Solo para Administrador */}
                {isAdmin && (
                  <button
                    onClick={() => {
                      if (!statusDetail.trim()) {
                        toast.error('Por favor ingresa los detalles del estado final');
                        return;
                      }
                      if (!selectedShipment?.manifest) {
                        toast.error('Por favor selecciona un envío');
                        return;
                      }
                      
                      // Check for pending events before opening complete shipment modal
                      if (!checkForPendingEvents()) {
                        return;
                      }
                      
                      setShowCompleteShipmentModal(true);
                    }}
                    disabled={isSubmitting || !statusDetail.trim()}
                    className="flex items-center px-6 py-2 text-white font-medium bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Finaliza carga y envía correos
                  </button>
                )}

              </div>
            </div>
          </div>
        )}

        {/* Recipients Preview Modal */}
        <EventUpdatesRecipientsModal
          isOpen={showRecipientsPreview}
          onClose={() => {
            setShowRecipientsPreview(false);
            // Si el modal se cierra durante el proceso, resetear isSubmitting
            if (isSubmitting) {
              setIsSubmitting(false);
            }
          }}
          shipment={selectedShipment}
          statusDetail={statusDetail}
          onConfirm={async (selectedRecipients) => {
            // NO cerrar el modal inmediatamente - dejarlo abierto para mostrar spinner
            // El modal se cerrará automáticamente cuando el proceso termine
            try {
              await handleSubmit(true, true, selectedRecipients);
              // Solo cerrar modal si todo fue exitoso
              setShowRecipientsPreview(false);
            } catch (error) {
              console.error('Error in modal onConfirm:', error);
              // En caso de error, el modal debe cerrarse y mostrar el error
              setShowRecipientsPreview(false);
            }
          }}
          user={user}
        />

        {/* Complete Shipment Recipients Modal */}
        <CompleteShipmentRecipientsModal
          isOpen={showCompleteShipmentModal}
          onClose={() => {
            setShowCompleteShipmentModal(false);
            // Si el modal se cierra durante el proceso, resetear isSubmitting
            if (isSubmitting) {
              setIsSubmitting(false);
            }
          }}
          shipment={selectedShipment}
          statusDetail={statusDetail}
          onConfirm={async (selectedRecipients) => {
            // NO cerrar el modal inmediatamente - dejarlo abierto para mostrar spinner
            // El modal se cerrará automáticamente cuando el proceso termine
            try {
              await handleCompleteShipment(selectedRecipients);
              // Solo cerrar modal si todo fue exitoso
              setShowCompleteShipmentModal(false);
            } catch (error) {
              console.error('Error in complete shipment modal onConfirm:', error);
              // En caso de error, el modal debe cerrarse y mostrar el error
              setShowCompleteShipmentModal(false);
            }
          }}
          user={user}
        />

        {/* Pending Events Warning Modal */}
        <PendingEventsWarningModal
          isOpen={showPendingEventsModal}
          onClose={() => setShowPendingEventsModal(false)}
          manifest={pendingEventsData?.manifest || ''}
          pendingEvents={pendingEventsData?.pendingEvents || []}
          pendingEventsCount={pendingEventsData?.pendingEventsCount || 0}
        />
      </div>
    </DashboardLayout>
  );
};

export default EventUpdates;